import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Close } from '@assets/icons';
import {
  ApplicationQuestionsData,
  ParsedAttachmentQuestion,
} from '@hooks/jobsBoard/useApplyToApplicationJob';
import { StyledGapContainer } from '@views/auth/signup/styles';
import { UploadBoxContentAttachment } from '@views/jobs/components/common';

import {
  StyledAttachmentsContainer,
  StyledCloseButton,
  StyledImageContainer,
  StyledLabel,
  StyledSpin,
  StyledUpload,
  StyledUploadButton,
} from '../styles';

type AttachmentQuestionProps = {
  question: ParsedAttachmentQuestion;
  handleFileUpload: ApplicationQuestionsData['handleFileUpload'];
  fileIdsLoading: ApplicationQuestionsData['columnIdToFileIdsLoading'][number];
  attachmentUrls: ApplicationQuestionsData['columnIdToAttachmentUrls'][number];
  onFileRemove: ApplicationQuestionsData['onFileRemove'];
  getAttachmentQuestionErrorMessage: ApplicationQuestionsData['getAttachmentQuestionErrorMessage'];
  wasSubmitButtonClicked: ApplicationQuestionsData['wasSubmitButtonClicked'];
};
export const AttachmentQuestion = ({
  question,
  handleFileUpload,
  fileIdsLoading,
  attachmentUrls,
  onFileRemove,
  getAttachmentQuestionErrorMessage,
  wasSubmitButtonClicked,
}: AttachmentQuestionProps) => {
  const isColumnIdLoading = useMemo(() => {
    return !!fileIdsLoading?.length;
  }, [fileIdsLoading?.length]);

  const isUploadDisabled = useMemo(() => {
    return (
      isColumnIdLoading ||
      (Number.isFinite(question?.uploadMax) &&
        question?.uploadMax > 0 &&
        !!attachmentUrls?.length &&
        attachmentUrls?.length >= question?.uploadMax)
    );
  }, [isColumnIdLoading, question?.uploadMax, attachmentUrls?.length]);

  const attachmentErrorMessage = useMemo(() => {
    return getAttachmentQuestionErrorMessage(question);
  }, [question, getAttachmentQuestionErrorMessage]);

  const [wasAttachmentChanged, setWasAttachmentChanged] = useState(false);

  const isErrorMessageAllowed = useMemo(() => {
    return (
      !!attachmentErrorMessage &&
      (wasSubmitButtonClicked || wasAttachmentChanged) &&
      !isColumnIdLoading
    );
  }, [
    attachmentErrorMessage,
    wasSubmitButtonClicked,
    wasAttachmentChanged,
    isColumnIdLoading,
  ]);

  return (
    <StyledGapContainer gap={8}>
      <StyledLabel label={question.label} requiredSymbol={question.required} />
      {question?.descriptionImage && (
        <StyledImageContainer>
          <img src={question.descriptionImage} alt={question.label} />
        </StyledImageContainer>
      )}
      {isColumnIdLoading ? (
        <StyledSpin />
      ) : (
        !!attachmentUrls?.length && (
          <StyledAttachmentsContainer>
            {attachmentUrls.map((url) => (
              <div key={`attachment-preview-${url}`} className='preview'>
                <StyledCloseButton
                  onClick={() => onFileRemove(url, question?.listColumnId)}
                >
                  <Close />
                </StyledCloseButton>
                <div className='img-container'>
                  <img src={url} alt={url} />
                </div>
              </div>
            ))}
          </StyledAttachmentsContainer>
        )
      )}
      <StyledUpload
        showUploadList={false}
        accept='.png,.jpeg,.jpg'
        multiple={true}
        beforeUpload={() => {
          return false; // Prevent default upload behavior
        }}
        onChange={(info) => {
          setWasAttachmentChanged(true);
          handleFileUpload(info, question?.listColumnId);
        }}
      >
        <StyledUploadButton disabled={isUploadDisabled}>
          <UploadBoxContentAttachment />
        </StyledUploadButton>
      </StyledUpload>
      {isErrorMessageAllowed && (
        <StyledErrorMessage> {attachmentErrorMessage}</StyledErrorMessage>
      )}
    </StyledGapContainer>
  );
};

const StyledErrorMessage = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.palette.color[0]};
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`;
